"use client"

import { ReactNode, useMemo, useState } from "react"
import type { Team, User } from "@/schema"
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@ui/shad"
import { cn } from "@ui/utils"
import { ChevronsUpDown, Users, X, type LucideIcon } from "lucide-react"

import { useFetchTeams } from "@/hooks/api/teams"
import { useFetchUsers } from "@/hooks/api/users"
import { AssignedTo } from "@/components/users/assigned-to"
import { UserAvatar } from "@/components/users/user-avatar"

import { BaseInlineInput } from "../base"

export interface InlineAssignmentSelectProps {
  id?: string
  bookId?: string
  label?: ReactNode
  LabelIcon?: LucideIcon
  selectedUserId: string | null
  selectedTeamId: string | null
  onSave: (userId: string | null, teamId: string | null, team?: Team) => void | Promise<void>
  isDisabled?: boolean
  fixedLabelWidth?: string
  hideLabel?: boolean
  size?: "sm" | "base" | "lg"
  className?: string
  variant?: "inline" | "field"
}

export function InlineAssignmentSelect({
  id,
  bookId,
  label,
  LabelIcon,
  selectedUserId,
  selectedTeamId,
  onSave,
  isDisabled,
  fixedLabelWidth,
  hideLabel,
  size,
  className,
  variant = "inline",
}: InlineAssignmentSelectProps) {
  const [open, setOpen] = useState(false)
  const [searchQuery, setSearchQuery] = useState("")

  const { data: usersData, isLoading: isLoadingUsers } = useFetchUsers(bookId)
  const { data: teamsData, isLoading: isLoadingTeams } = useFetchTeams({ bookIdOverride: bookId })

  const users = useMemo(() => usersData?.users ?? [], [usersData?.users])
  const teams = useMemo(() => teamsData?.teams ?? [], [teamsData?.teams])

  const selectedUser = useMemo(() => users.find((u) => u.id === selectedUserId), [users, selectedUserId])
  const selectedTeam = useMemo(() => teams.find((t) => t.id === selectedTeamId), [teams, selectedTeamId])

  const filteredUsers = useMemo(() => {
    const query = searchQuery.toLowerCase()
    const filtered = query
      ? users.filter((u) => u.full_name?.toLowerCase().includes(query) || u.email?.toLowerCase().includes(query))
      : users
    return [...filtered].sort((a, b) => (a.full_name ?? "").localeCompare(b.full_name ?? ""))
  }, [users, searchQuery])

  const filteredTeams = useMemo(() => {
    const query = searchQuery.toLowerCase()
    const filtered = query ? teams.filter((t) => t.name?.toLowerCase().includes(query)) : teams
    return [...filtered].sort((a, b) => (a.name ?? "").localeCompare(b.name ?? ""))
  }, [teams, searchQuery])

  const hasAssignment = selectedUserId || selectedTeamId
  const hasResults = filteredUsers.length > 0 || filteredTeams.length > 0
  const isLoading = isLoadingUsers || isLoadingTeams

  const handleSelectUser = async (user: User) => {
    await onSave(user.id, null)
    setOpen(false)
  }

  const handleSelectTeam = async (team: Team) => {
    await onSave(null, team.id, team)
    setOpen(false)
  }

  const handleClear = async () => {
    await onSave(null, null)
    setOpen(false)
  }

  const renderValue = () => {
    if (selectedTeam) {
      return (
        <div className="inline-flex min-w-0 items-center gap-1.5">
          {selectedTeam.color && (
            <div className="size-3 flex-none rounded-full" style={{ backgroundColor: selectedTeam.color }} />
          )}
          <Users className="size-3.5 flex-none" />
          <span className="truncate">{selectedTeam.name}</span>
        </div>
      )
    }
    if (selectedUser) {
      return <AssignedTo userId={selectedUser.id} size="sm" />
    }
    return <span className="text-input-placeholder">-</span>
  }

  return (
    <BaseInlineInput
      label={label}
      LabelIcon={LabelIcon}
      hideLabel={hideLabel}
      fixedLabelWidth={fixedLabelWidth}
      size={size}
      className={className}
    >
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            id={id}
            type="button"
            className={cn(
              "focus:outline-primary flex w-full items-center rounded-md text-left focus:outline-1 md:text-sm",
              variant === "field"
                ? "border-input bg-background h-9 justify-between border px-4 py-2 shadow-[var(--shadow-btn-raised-light)] transition-[box-shadow,border-color] duration-100 ease-out active:translate-y-px active:shadow-none"
                : "h-8 grow justify-start border border-transparent px-2 shadow-none",
              isDisabled
                ? "cursor-default opacity-50"
                : variant === "field"
                  ? "hover:bg-accent hover:text-accent-foreground cursor-pointer"
                  : "hover:bg-muted cursor-pointer",
              className
            )}
            disabled={isDisabled}
          >
            {renderValue()}
            {variant === "field" && <ChevronsUpDown className="text-icon ml-2 size-4 shrink-0 opacity-50" />}
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-[250px] p-0" align="start">
          <Command shouldFilter={false}>
            <CommandInput placeholder="Search users or teams..." value={searchQuery} onValueChange={setSearchQuery} />
            <CommandList className="max-h-[300px]">
              {searchQuery && !hasResults && !isLoading && (
                <CommandEmpty className="text-muted-foreground py-3 text-center text-sm">No results found</CommandEmpty>
              )}

              {isLoading && <CommandItem disabled>Loading...</CommandItem>}

              {filteredUsers.length > 0 && (
                <CommandGroup heading="Users">
                  {filteredUsers.map((user) => (
                    <CommandItem
                      key={user.id}
                      value={`user-${user.id}`}
                      onSelect={() => handleSelectUser(user)}
                      className="flex items-center gap-2"
                    >
                      <UserAvatar userId={user.id} size="sm" showName />
                    </CommandItem>
                  ))}
                </CommandGroup>
              )}

              {filteredTeams.length > 0 && (
                <CommandGroup heading="Teams">
                  {filteredTeams.map((team) => (
                    <CommandItem
                      key={team.id}
                      value={`team-${team.id}`}
                      onSelect={() => handleSelectTeam(team)}
                      className="flex items-center gap-2"
                    >
                      <div className="flex items-center gap-2">
                        {team.color && (
                          <div className="size-3 flex-none rounded-full" style={{ backgroundColor: team.color }} />
                        )}
                        <Users className="size-3.5 flex-none" />
                        <span className="truncate">{team.name}</span>
                      </div>
                    </CommandItem>
                  ))}
                </CommandGroup>
              )}

              {hasAssignment && (
                <CommandGroup>
                  <CommandItem value="__remove__" onSelect={handleClear}>
                    <X />
                    Remove assignee
                  </CommandItem>
                </CommandGroup>
              )}
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </BaseInlineInput>
  )
}
